/**
 * 对应后端 server/src/contracts/ 的全量 TypeScript 契约定义
 */

export interface ArtifactRef {
  name: string;
  url: string;
  mime_type: string;
}

export type TaskStatus = "completed" | "failed" | "cancelled" | "timed_out" | "skipped";

export interface TaskResultContract {
  protocol_version: number;
  task_id: string;
  run_id?: string;
  status: TaskStatus;
  result: string;
  artifacts: ArtifactRef[];
  error?: string;
  retryable: boolean;
}

export type EventType =
  | "run.started"
  | "token.delta"
  | "tool.started"
  | "tool.completed"
  | "task.started"
  | "task.completed"
  | "interrupt.required"
  | "run.completed"
  | "run.failed";

export interface StreamEventContract {
  protocol_version: number;
  event_id: string;
  run_id: string;
  sequence: number;
  type: EventType;
  payload: Record<string, any>;
}

export interface ChatMessageContract {
  protocol_version: number;
  id: string;
  request_id?: string | null;
  run_id: string | null;
  role: "user" | "assistant" | "system";
  content: string;
  artifacts: ArtifactRef[];
  status: "completed";
  created_at: string;
}

export interface PersistenceFailedEventContract {
  type: "persistence_failed";
  code: "history_persistence_failed";
  detail: string;
  request_id: string;
  run_id: string;
  message_id: string;
  retryable: boolean;
}

export type RunFailureStatus = "failed" | "timed_out" | "cancelled";

export interface RunFailedEventContract {
  protocol_version: number;
  event_id: string;
  run_id: string;
  sequence: number;
  type: "run.failed";
  payload: {
    status: RunFailureStatus;
    code: string;
    detail: string;
    request_id: string;
    message_id: string;
    retryable: boolean;
  };
}

type UnknownRecord = Record<string, unknown>;

const asRecord = (value: unknown): UnknownRecord | null =>
  typeof value === "object" && value !== null ? (value as UnknownRecord) : null;

export const isPersistenceFailedEvent = (
  value: unknown
): value is PersistenceFailedEventContract => {
  const record = asRecord(value);

  return (
    record !== null &&
    record.type === "persistence_failed" &&
    record.code === "history_persistence_failed" &&
    typeof record.detail === "string" &&
    typeof record.request_id === "string" &&
    typeof record.run_id === "string" &&
    typeof record.message_id === "string" &&
    typeof record.retryable === "boolean"
  );
};

export const isRunFailedEvent = (
  value: unknown
): value is RunFailedEventContract => {
  const record = asRecord(value);
  const payload = record ? asRecord(record.payload) : null;
  const status = payload?.status;

  return (
    record !== null &&
    payload !== null &&
    typeof record.protocol_version === "number" &&
    typeof record.event_id === "string" &&
    typeof record.run_id === "string" &&
    typeof record.sequence === "number" &&
    record.type === "run.failed" &&
    (status === "failed" || status === "timed_out" || status === "cancelled") &&
    typeof payload.code === "string" &&
    typeof payload.detail === "string" &&
    typeof payload.request_id === "string" &&
    typeof payload.message_id === "string" &&
    typeof payload.retryable === "boolean"
  );
};

export const normalizeChatMessage = (
  value: unknown
): ChatMessageContract | null => {
  const record = asRecord(value);

  if (
    !record ||
    record.protocol_version !== 1 ||
    typeof record.id !== "string" ||
    record.id.length === 0
  ) {
    return null;
  }

  const role = record.role;
  if (role !== "user" && role !== "assistant" && role !== "system") {
    return null;
  }

  if (
    typeof record.content !== "string" ||
    typeof record.created_at !== "string" ||
    !Array.isArray(record.artifacts)
  ) {
    return null;
  }

  if (record.status !== "completed") {
    return null;
  }

  return {
    protocol_version: 1,
    id: record.id,
    request_id: typeof record.request_id === "string" ? record.request_id : null,
    run_id: typeof record.run_id === "string" ? record.run_id : null,
    role,
    content: record.content,
    artifacts: record.artifacts as ArtifactRef[],
    status: "completed",
    created_at: record.created_at,
  };
};

export const normalizeChatMessages = (value: unknown): ChatMessageContract[] =>
  Array.isArray(value)
    ? value
        .map(normalizeChatMessage)
        .filter((message): message is ChatMessageContract => message !== null)
    : [];
